import {
  DEFAULT_PRICE_BASIS,
  MIN_AUCTION_YEAR,
  getMaxAuctionYear,
  isDateWindow,
  isAuctionHouse,
  isAuctionMarket,
  isPriceBasis,
  isVerifiedPriceBasis,
  type TAuctionHouse,
  type TAuctionMarket,
  type TDateWindow,
  type TPriceBasis,
} from './auctionConfig';

/**
 * Visitor filters → provider query parameters (spec 006, research.md R3).
 *
 * WHY THIS IS AN ALLOWLIST AND NOT A PASS-THROUGH:
 * the provider silently IGNORES filter parameters it does not recognise and
 * returns HTTP 200 with unfiltered data. Verified — `not_a_real_filter=zzz`
 * returned the full 154k-lot set, and `vin=<a Nissan's VIN>` returned a Land
 * Rover. Note the asymmetry: parameters it *does* know are validated properly
 * (`per_page=100` → 422), so a successful response is no evidence that a filter
 * was understood.
 *
 * The consequence is that a typo here would show visitors unfiltered inventory
 * labelled as filtered — a silent correctness failure of exactly the kind
 * constitution Principle I forbids. Nothing reaches the provider except the
 * mapped values built below.
 */

/**
 * Polish search-param names, matching the site's other public routes.
 *
 * Every entry here maps to a provider parameter that was VERIFIED to actually
 * filter — by checking the returned rows, not merely that the total moved.
 * Notably absent: `odometer_from`/`odometer_to`, which the vendor documents and
 * the API accepts with HTTP 200 — while returning the full unfiltered
 * 154,450-lot set. Mileage filtering is therefore not offered rather than offered
 * and silently broken. Same for fuel, colour, transmission, drive and
 * primary_damage, none of which are honoured on this endpoint.
 */
export const FILTER_PARAMS = {
  make: 'marka',
  model: 'model',
  yearFrom: 'rokOd',
  yearTo: 'rokDo',
  market: 'rynek',
  house: 'dom',
  priceBasis: 'kwota',
  priceFrom: 'cenaOd',
  priceTo: 'cenaDo',
  buyNowOnly: 'kupTeraz',
  auctionWindow: 'termin',
  /** Listing mode: absent = live, `sprzedane` = sold (spec 015). */
  mode: 'stan',
  /** A lot number or a VIN (or its prefix) to look up directly. */
  lookup: 'szukaj',
  page: 'page',
  /** Listing order (spec 017); absent = auction date, nearest first. */
  sort: 'sort',
} as const;

export type TLotSort = 'date_asc' | 'date_desc' | 'price_asc' | 'price_desc';
export const DEFAULT_LOT_SORT: TLotSort = 'date_asc';
export const LOT_SORT_OPTIONS: ReadonlyArray<{ value: TLotSort; label: string }> = [
  { value: 'date_asc', label: 'Data aukcji: najbliższe' },
  { value: 'date_desc', label: 'Data aukcji: najdalsze' },
  { value: 'price_asc', label: 'Cena: rosnąco' },
  { value: 'price_desc', label: 'Cena: malejąco' },
];

/**
 * The date sorts are named by DISTANCE FROM TODAY, and in sold mode the store
 * inverts them so the nearest sale is the newest one (`orderBy` in
 * src/inventory/queries.ts). The value is therefore right in both modes but the
 * wording is not: on ended auctions "najbliższe" reads as the oldest sale when
 * it delivers the most recent one, and an owner reported exactly that confusion
 * on 2026-09-15 after checking page 524 and finding August. Only the labels
 * change here — the values stay `date_asc` / `date_desc`, so every address and
 * bookmark already out there keeps working.
 */
const SOLD_DATE_LABELS: Partial<Record<TLotSort, string>> = {
  date_asc: 'Data sprzedaży: najnowsze',
  date_desc: 'Data sprzedaży: najstarsze',
};

export const lotSortOptions = (mode: TListingMode): ReadonlyArray<{ value: TLotSort; label: string }> =>
  mode === 'sold'
    ? LOT_SORT_OPTIONS.map(option => ({ ...option, label: SOLD_DATE_LABELS[option.value] ?? option.label }))
    : LOT_SORT_OPTIONS;
export const isLotSort = (value: string): value is TLotSort => LOT_SORT_OPTIONS.some(option => option.value === value);
/**
 * Page numbers stop here: beyond it the address is a crawl trap, not a position anyone reaches
 * (spec 017). It was 500 until 2026-09-13, when the live listing reached 3,866 pages and every
 * deeper address rendered page 500 under its own number; 10,000 pages is 240,000 lots, more than
 * the store will hold live, and page 3,866 costs the store about 350 ms (research 017 R24).
 */
export const MAX_LISTING_PAGE = 10_000;

export type TListingMode = 'live' | 'now' | 'sold';
export const SOLD_MODE_VALUE = 'sprzedane';
export const NOW_MODE_VALUE = 'na-zywo';
/** The address value for each mode; live is the default and is never written. */
export const MODE_VALUES: Record<Exclude<TListingMode, 'live'>, string> = { now: NOW_MODE_VALUE, sold: SOLD_MODE_VALUE };
export const LISTING_MODE_OPTIONS: ReadonlyArray<{ value: TListingMode; label: string }> = [
  { value: 'live', label: 'Aktualne' },
  { value: 'now', label: 'Na żywo' },
  { value: 'sold', label: 'Sprzedane' },
];

export type TLotFilters = {
  /** Upcoming auctions (default), auctions running right now, or confirmed sales from the last month (spec 015). */
  mode: TListingMode;
  /** A lot number or VIN typed into the form; when set, every other filter is ignored. */
  lookup: string | null;
  make: string | null;
  model: string | null;
  yearFrom: number | null;
  yearTo: number | null;
  /** Empty means both. Determines the currency every amount is quoted in. */
  markets: TAuctionMarket[];
  /** Empty means both. */
  houses: TAuctionHouse[];
  priceBasis: TPriceBasis;
  priceFrom: number | null;
  priceTo: number | null;
  buyNowOnly: boolean;
  /** How soon the auction runs, as a preset window. */
  auctionWindow: TDateWindow | null;
  /** 1-based page number (spec 017, the store's numbered paging); 1 when absent or malformed. */
  page: number;
  /** Listing order (spec 017). */
  sort: TLotSort;
};

const MAX_MAKE_LENGTH = 40;
const MAX_PRICE = 10_000_000;

const parseYear = (value: string | undefined): number | null => {
  if (!value) return null;

  const year = Number(value);

  if (!Number.isInteger(year) || year < MIN_AUCTION_YEAR || year > getMaxAuctionYear()) return null;

  return year;
};

/**
 * Make and model are free-text trade names. Allow letters, digits, spaces and the
 * punctuation that genuinely occurs in them (BMW "X5", Mercedes "S550",
 * "F-350", "MX-5 MIATA", "1500 CLASSIC"), and reject anything else as a typo.
 */
const parseName = (value: string | undefined): string | null => {
  if (!value) return null;

  const name = value.trim().slice(0, MAX_MAKE_LENGTH);

  return /^[\p{L}\d\s&./-]+$/u.test(name) ? name : null;
};

const parseAmount = (value: string | undefined): number | null => {
  if (!value) return null;

  const amount = Number(value);

  return Number.isFinite(amount) && amount >= 0 && amount <= MAX_PRICE ? Math.round(amount) : null;
};

/**
 * Reads raw search params into a validated filter object. Anything unrecognised,
 * malformed or out of range is dropped, and the page renders as if it were unset
 * rather than erroring (contracts/routes.md R1).
 */
export const parseLotFilters = (
  searchParams: Record<string, string | string[] | undefined>
): TLotFilters => {
  const single = (key: string): string | undefined => {
    const value = searchParams[key];
    return Array.isArray(value) ? value[0] : value;
  };

  const many = (key: string): string[] => {
    const value = searchParams[key];
    return Array.isArray(value) ? value : value ? [value] : [];
  };

  // Deduplicated: `?dom=copart&dom=copart` must not double every auction name.
  const markets = [...new Set(many(FILTER_PARAMS.market).filter(isAuctionMarket))];
  const houses = [...new Set(many(FILTER_PARAMS.house).filter(isAuctionHouse))];

  let yearFrom = parseYear(single(FILTER_PARAMS.yearFrom));
  let yearTo = parseYear(single(FILTER_PARAMS.yearTo));

  // The provider rejects a `_to` without its matching `_from`, and an inverted
  // range is a user error rather than a provider one — normalise both here.
  if (yearFrom && yearTo && yearFrom > yearTo) [yearFrom, yearTo] = [yearTo, yearFrom];

  let priceFrom = parseAmount(single(FILTER_PARAMS.priceFrom));
  let priceTo = parseAmount(single(FILTER_PARAMS.priceTo));

  if (priceFrom !== null && priceTo !== null && priceFrom > priceTo) {
    [priceFrom, priceTo] = [priceTo, priceFrom];
  }

  /*
   * A basis nothing can actually filter on is DROPPED, not carried through.
   *
   * `apibaraFilters` maps a price range onto the current bid and nothing else,
   * so `oferta` is the only basis with a query behind it. Accepting the other
   * two — both servable by the provider spec 006 was written against — meant a
   * stale `?kwota=wartosc` link kept its label all the way to the chip row and
   * announced "Wartość rynkowa (szac.): 20 000–21 000" over results that had
   * been narrowed by the current bid instead. That is exactly the mislabelling
   * FR-011 forbids, and it is worse than a dropped filter because the visitor
   * has no way to see it.
   */
  const rawBasis = single(FILTER_PARAMS.priceBasis);
  const priceBasis =
    rawBasis && isPriceBasis(rawBasis) && isVerifiedPriceBasis(rawBasis)
      ? rawBasis
      : DEFAULT_PRICE_BASIS;



  const rawAuctionWindow = single(FILTER_PARAMS.auctionWindow);

  const page = Number(single(FILTER_PARAMS.page) ?? 1);

  const rawLookup = single(FILTER_PARAMS.lookup)?.trim().toUpperCase() ?? '';
  const rawSort = single(FILTER_PARAMS.sort);

  return {
    mode:
      single(FILTER_PARAMS.mode) === SOLD_MODE_VALUE
        ? 'sold'
        : single(FILTER_PARAMS.mode) === NOW_MODE_VALUE
          ? 'now'
          : 'live',
    // Lot numbers are digits; VINs are 17 characters without I, O or Q. A prefix
    // of a VIN is accepted because the provider's search is a prefix search.
    lookup: /^[A-HJ-NPR-Z0-9-]{3,20}$/.test(rawLookup) ? rawLookup : null,
    make: parseName(single(FILTER_PARAMS.make)),
    model: parseName(single(FILTER_PARAMS.model)),
    yearFrom,
    yearTo,
    markets,
    houses,
    priceBasis,
    priceFrom,
    priceTo,
    buyNowOnly: single(FILTER_PARAMS.buyNowOnly) === '1',
    auctionWindow: rawAuctionWindow && isDateWindow(rawAuctionWindow) ? rawAuctionWindow : null,
    /*
     * A real page number again with the store (spec 017): 1-based, clamped to
     * MAX_LISTING_PAGE. On the provider path it is only recognised so a legacy
     * link can be redirected rather than silently ignored.
     */
    page: Number.isInteger(page) && page >= 1 ? Math.min(page, MAX_LISTING_PAGE) : 1,
    sort: rawSort && isLotSort(rawSort) ? rawSort : DEFAULT_LOT_SORT,
  };
};

/** Round-trips filter state back into the URL so a filtered view is shareable (FR-015). */
export const toSearchParams = (filters: TLotFilters): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.mode !== 'live') params.set(FILTER_PARAMS.mode, MODE_VALUES[filters.mode]);
  if (filters.lookup) params.set(FILTER_PARAMS.lookup, filters.lookup);
  if (filters.make) params.set(FILTER_PARAMS.make, filters.make);
  if (filters.model) params.set(FILTER_PARAMS.model, filters.model);
  if (filters.yearFrom) params.set(FILTER_PARAMS.yearFrom, String(filters.yearFrom));
  if (filters.yearTo) params.set(FILTER_PARAMS.yearTo, String(filters.yearTo));
  if (filters.priceFrom !== null) params.set(FILTER_PARAMS.priceFrom, String(filters.priceFrom));
  if (filters.priceTo !== null) params.set(FILTER_PARAMS.priceTo, String(filters.priceTo));

  // The basis only means anything alongside a bound, so it stays out of the URL
  // otherwise — an inert `?kwota=wartosc` would read as an active filter in the
  // chip list and in shared links.
  if (
    filters.priceBasis !== DEFAULT_PRICE_BASIS &&
    (filters.priceFrom !== null || filters.priceTo !== null)
  ) {
    params.set(FILTER_PARAMS.priceBasis, filters.priceBasis);
  }

  if (filters.buyNowOnly) params.set(FILTER_PARAMS.buyNowOnly, '1');
  if (filters.auctionWindow) params.set(FILTER_PARAMS.auctionWindow, filters.auctionWindow);
  if (filters.sort !== DEFAULT_LOT_SORT) params.set(FILTER_PARAMS.sort, filters.sort);

  filters.markets.forEach(market => params.append(FILTER_PARAMS.market, market));
  filters.houses.forEach(house => params.append(FILTER_PARAMS.house, house));

  return params;
};
